import type { DisplayModel } from './display-models'

import { useEventListener, useLocalStorage } from '@vueuse/core'
import { converter } from 'culori'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

import { useIndexedDBStorage, waitForStorage } from '../utils/indexed-db-storage'
import { useAudioDevice } from './audio'
import { DisplayModelFormat, useDisplayModelsStore } from './display-models'

export const DEFAULT_THEME_COLORS_HUE = 220.44

/**
 * 标记值：用户明确选择"不使用模型"
 * 使用字符串而非 null，因为 useLocalStorage 无法正确持久化 null
 * （null 会导致 localStorage key 被删除，页面重新加载时回退到默认值）
 */
export const DISPLAY_MODEL_NONE = '__none__'

const convert = converter('oklch')
const getHueFrom = (color?: string) => color ? convert(color)?.h : DEFAULT_THEME_COLORS_HUE

export const useSettings = defineStore('settings', () => {
  const displayModelsStore = useDisplayModelsStore()

  // 固定使用简体中文
  const language = ref('zh-Hans')

  const stageModelSelected = useLocalStorage<string>('settings/stage/model', 'preset-live2d-pro')
  const stageModelSelectedDisplayModel = ref<DisplayModel | undefined>()
  const stageModelSelectedUrl = ref<string>()
  const stageModelRenderer = ref<'live2d' | 'vrm' | 'sprite' | 'disabled'>()

  async function updateStageModel() {
    if (!stageModelSelected.value || stageModelSelected.value === DISPLAY_MODEL_NONE) {
      stageModelSelectedUrl.value = undefined
      stageModelSelectedDisplayModel.value = undefined
      stageModelRenderer.value = 'disabled'
      return
    }

    const model = await displayModelsStore.getDisplayModel(stageModelSelected.value)
    if (!model) {
      stageModelSelectedUrl.value = undefined
      stageModelSelectedDisplayModel.value = undefined
      stageModelRenderer.value = 'disabled'
      return
    }

    switch (model.format) {
      case DisplayModelFormat.Live2dZip:
      case DisplayModelFormat.Live2dV2Zip:
      case DisplayModelFormat.Live2dDirectory:
        stageModelRenderer.value = 'live2d'
        break
      case DisplayModelFormat.VRM:
        stageModelRenderer.value = 'vrm'
        break
      case DisplayModelFormat.SpriteSet:
        stageModelRenderer.value = 'sprite'
        break
      default:
        stageModelRenderer.value = 'disabled'
        break
    }

    if (model.type === 'file') {
      if (stageModelSelectedUrl.value) {
        URL.revokeObjectURL(stageModelSelectedUrl.value)
      }

      stageModelSelectedUrl.value = URL.createObjectURL(model.file)
    }
    else {
      stageModelSelectedUrl.value = model.url
    }

    stageModelSelectedDisplayModel.value = model
  }

  async function initializeStageModel() {
    await updateStageModel()
  }

  useEventListener('pagehide', () => {
    if (stageModelSelectedUrl.value) {
      URL.revokeObjectURL(stageModelSelectedUrl.value)
    }
  })

  const stageViewControlsEnabled = ref(false)
  const stageModelVisible = useLocalStorage('settings/stage/model-visible', true)

  const live2dDisableFocus = useLocalStorage('settings/live2d/disable-focus', false)

  const disableTransitions = useLocalStorage('settings/disable-transitions', true)
  const usePageSpecificTransitions = useLocalStorage('settings/use-page-specific-transitions', true)

  const themeColorsHue = useLocalStorage('settings/theme/colors/hue', DEFAULT_THEME_COLORS_HUE)
  const themeColorsHueDynamic = useLocalStorage('settings/theme/colors/hue-dynamic', false)

  const allowVisibleOnAllWorkspaces = useLocalStorage('settings/allow-visible-on-all-workspaces', true)

  // Chat bubble font size (in rem, default 1rem = base size)
  const chatBubbleFontSize = useLocalStorage('settings/chat/bubble-font-size', 1)

  // Mobile chat history max height (in dvh, default 45dvh)
  const mobileChatHistoryMaxHeight = useLocalStorage('settings/chat/mobile-history-max-height', 45)

  // 发送给AI的最大上下文消息数量（不含system消息，0表示不限制）
  const maxContextMessages = useLocalStorage('settings/chat/max-context-messages', 128)

  // AI回复防抖延迟（毫秒，用户停止输入后等待多久触发AI回复）
  const aiReplyDebounceDelay = useLocalStorage('settings/chat/ai-reply-debounce-delay', 3000)

  // 'off' 保留为默认档：不传思考参数，由模型或服务商决定默认行为。
  const thinkingLevel = useLocalStorage<'off' | 'low' | 'medium' | 'high'>('settings/chat/thinking-level', 'off')

  // Chat bubble style settings
  const chatBubbleTheme = useLocalStorage<'default' | 'latte' | 'qq' | 'wechat' | 'piggy' | 'glass' | 'blue-pink' | 'custom'>('settings/chat/bubble-theme', 'default')
  const chatBubbleCustomCss = useLocalStorage<string | undefined>('settings/chat/bubble-custom-css', undefined)

  // User-saved bubble presets
  const chatBubbleUserPresets = useLocalStorage<Array<{ id: string, name: string, css: string }>>('settings/chat/bubble-user-presets', [])

  // Custom font
  const customFontUrl = useLocalStorage<string | undefined>('settings/custom-font-url', undefined)
  // ✨ 字体 base64 数据迁移到 IndexedDB（可能数 MB，localStorage 放不下）
  const customFontDataUrlStorage = useIndexedDBStorage<string | undefined>('settings/custom-font-data', undefined)
  const customFontDataUrl = customFontDataUrlStorage.data
  const customFontFileName = useLocalStorage<string | undefined>('settings/custom-font-filename', undefined)
  const customFontSource = ref<'url' | 'file'>('url')

  // 字体跟随系统：开启后系统字体优先（小赖体兜底），关闭则默认小赖体优先
  const fontFollowSystem = useLocalStorage<boolean>('settings/font-follow-system', false)

  // Initialize font from stored data (等待 IndexedDB 加载完成)
  waitForStorage(customFontDataUrlStorage).then(() => {
    if (customFontDataUrl.value) {
      customFontSource.value = 'file'
      console.info('[Settings Store] Font restored from IndexedDB')
    }
  })
  watch(customFontDataUrl, (dataUrl) => {
    if (dataUrl) {
      customFontSource.value = 'file'
    }
  })

  // Custom stage background
  const stageCustomBackgroundEnabled = useLocalStorage('settings/stage/custom-background-enabled', false)
  // ✨ 背景图 base64 数据迁移到 IndexedDB（可能数 MB，localStorage 放不下）
  const stageCustomBackgroundDataUrlStorage = useIndexedDBStorage<string | undefined>('settings/stage/custom-background-data', undefined)
  const stageCustomBackgroundDataUrl = stageCustomBackgroundDataUrlStorage.data
  const stageCustomBackgroundFileName = useLocalStorage<string | undefined>('settings/stage/custom-background-filename', undefined)
  const stageCustomBackgroundUrl = ref<string | undefined>()
  const stageCustomBackgroundFile = ref<File | undefined>()

  // Initialize background URL from stored data (等待 IndexedDB 加载完成)
  waitForStorage(stageCustomBackgroundDataUrlStorage).then(() => {
    if (stageCustomBackgroundDataUrl.value) {
      stageCustomBackgroundUrl.value = stageCustomBackgroundDataUrl.value
      console.info('[Settings Store] Background restored from IndexedDB')
    }
  })
  watch(stageCustomBackgroundDataUrl, (dataUrl) => {
    if (dataUrl) {
      stageCustomBackgroundUrl.value = dataUrl
    }
  })

  function getFontMimeType(file: File): string {
    const extension = file.name.toLowerCase().split('.').pop()
    switch (extension) {
      case 'ttf':
        return 'font/ttf'
      case 'otf':
        return 'font/otf'
      case 'woff':
        return 'font/woff'
      case 'woff2':
        return 'font/woff2'
      default:
        return file.type || 'application/octet-stream'
    }
  }

  async function fileToDataUrl(file: File, mimeType = file.type): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        if (!mimeType || result.startsWith(`data:${mimeType};`)) {
          resolve(result)
          return
        }
        resolve(result.replace(/^data:[^;]*;/, `data:${mimeType};`))
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  async function validateFontDataUrl(dataUrl: string): Promise<void> {
    if (!('FontFace' in window))
      return

    try {
      const fontFace = new FontFace('custom-font-validation', `url(${JSON.stringify(dataUrl)})`)
      await fontFace.load()
    }
    catch {
      throw new Error('这个字体文件浏览器无法解析，请换一个标准的 TTF、OTF、WOFF 或 WOFF2 字体文件。')
    }
  }

  async function setCustomBackground(file: File | undefined) {
    console.info('[Settings Store] setCustomBackground called with:', file?.name)

    if (!file) {
      console.info('[Settings Store] Clearing background')
      stageCustomBackgroundEnabled.value = false
      stageCustomBackgroundFile.value = undefined
      stageCustomBackgroundDataUrl.value = undefined
      stageCustomBackgroundFileName.value = undefined
      stageCustomBackgroundUrl.value = undefined
      return
    }

    try {
      // Convert file to base64 data URL for persistence
      const dataUrl = await fileToDataUrl(file)

      stageCustomBackgroundFile.value = file
      stageCustomBackgroundDataUrl.value = dataUrl
      stageCustomBackgroundFileName.value = file.name
      stageCustomBackgroundUrl.value = dataUrl
      stageCustomBackgroundEnabled.value = true

      console.info('[Settings Store] Background set:', {
        enabled: stageCustomBackgroundEnabled.value,
        fileName: file.name,
        dataUrlLength: dataUrl.length,
      })
    }
    catch (error) {
      console.error('[Settings Store] Failed to set background:', error)
    }
  }

  async function setCustomFont(file: File | undefined) {
    console.info('[Settings Store] setCustomFont called with:', file?.name)

    if (!file) {
      console.info('[Settings Store] Clearing font')
      customFontDataUrl.value = undefined
      customFontFileName.value = undefined
      customFontSource.value = 'url'
      return
    }

    try {
      // Validate font file type
      const validTypes = ['font/ttf', 'font/otf', 'font/woff', 'font/woff2', 'application/x-font-ttf', 'application/x-font-otf', 'application/font-woff', 'application/font-woff2']
      const validExtensions = ['.ttf', '.otf', '.woff', '.woff2']
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))

      if (!validTypes.includes(file.type) && !hasValidExtension) {
        console.error('[Settings Store] Invalid font file type:', file.type)
        throw new Error('Invalid font file type. Please upload a .ttf, .otf, .woff, or .woff2 file.')
      }

      const fontMimeType = getFontMimeType(file)
      const dataUrl = await fileToDataUrl(file, fontMimeType)
      await validateFontDataUrl(dataUrl)

      customFontDataUrl.value = dataUrl
      customFontFileName.value = file.name
      customFontSource.value = 'file'

      console.info('[Settings Store] Font set:', {
        fileName: file.name,
        fileType: file.type,
        dataUrlLength: dataUrl.length,
      })
    }
    catch (error) {
      console.error('[Settings Store] Failed to set font:', error)
      throw error
    }
  }

  function setThemeColorsHue(hue = DEFAULT_THEME_COLORS_HUE) {
    themeColorsHue.value = hue
    themeColorsHueDynamic.value = false
  }

  function applyPrimaryColorFrom(color?: string) {
    setThemeColorsHue(getHueFrom(color))
  }

  /**
   * Check if a color is currently selected based on its hue value
   * @param hexColor Hex color code to check
   * @returns True if the color's hue matches the current theme hue
   */
  function isColorSelectedForPrimary(hexColor?: string) {
    // If dynamic coloring is enabled, no preset color is manually selected
    if (themeColorsHueDynamic.value)
      return false

    // Convert hex color to OKLCH
    const h = getHueFrom(hexColor)
    if (!h)
      return false

    // Compare hue values with a small tolerance for floating point comparison
    const hueDifference = Math.abs(h - themeColorsHue.value)
    return hueDifference < 0.01 || hueDifference > 359.99
  }

  useEventListener('pagehide', () => {
    if (stageCustomBackgroundUrl.value) {
      URL.revokeObjectURL(stageCustomBackgroundUrl.value)
    }
  })

  return {
    disableTransitions,
    usePageSpecificTransitions,
    language,

    stageModelRenderer,
    stageModelSelected,
    stageModelSelectedUrl,
    stageModelSelectedDisplayModel,
    stageViewControlsEnabled,
    stageModelVisible,

    live2dDisableFocus,
    themeColorsHue,
    themeColorsHueDynamic,

    allowVisibleOnAllWorkspaces,

    // Chat settings
    chatBubbleFontSize,
    mobileChatHistoryMaxHeight,
    maxContextMessages,
    aiReplyDebounceDelay,
    thinkingLevel,
    chatBubbleTheme,
    chatBubbleCustomCss,
    chatBubbleUserPresets,

    // Custom font
    customFontUrl,
    customFontDataUrl,
    customFontFileName,
    customFontSource,
    fontFollowSystem,
    setCustomFont,

    // Custom stage background
    stageCustomBackgroundEnabled,
    stageCustomBackgroundUrl,
    stageCustomBackgroundFile,
    stageCustomBackgroundFileName,
    stageCustomBackgroundDataUrl,
    setCustomBackground,

    setThemeColorsHue,
    applyPrimaryColorFrom,
    isColorSelectedForPrimary,
    initializeStageModel,
    updateStageModel,
  }
})

export const useSettingsAudioDevice = defineStore('settings-audio-devices', () => {
  const { audioInputs, deviceConstraints, selectedAudioInput: selectedAudioInputNonPersist, startStream, stopStream, stream, askPermission } = useAudioDevice()

  const selectedAudioInputPersist = useLocalStorage('settings/audio/input', selectedAudioInputNonPersist.value)
  const selectedAudioInputEnabledPersist = useLocalStorage('settings/audio/input-enabled', false)

  watch(selectedAudioInputPersist, (newValue) => {
    selectedAudioInputNonPersist.value = newValue
  })

  watch(selectedAudioInputEnabledPersist, (val) => {
    if (val) {
      startStream()
    }
    else {
      stopStream()
    }
  })

  // 初始化：恢复音频输入状态（不依赖 onMounted，store 可能在组件外创建）
  if (selectedAudioInputEnabledPersist.value && selectedAudioInputPersist.value) {
    startStream()
  }
  if (selectedAudioInputNonPersist.value && !selectedAudioInputEnabledPersist.value) {
    selectedAudioInputPersist.value = selectedAudioInputNonPersist.value
  }

  return {
    audioInputs,
    deviceConstraints,
    selectedAudioInput: selectedAudioInputPersist,
    enabled: selectedAudioInputEnabledPersist,

    stream,

    askPermission,
    startStream,
    stopStream,
  }
})
